// hooks/useDevToolsGuard.js
import { useEffect, useState } from "react";

/**
 * useDevToolsGuard — close the inspector's doors and notice when it is open
 * anyway. Returns true while developer tools are detected open.
 *
 * A web page cannot forbid developer tools: the browser menu always has them.
 * So this does the three things a page CAN do —
 *
 *   1. Shortcuts. F12, Ctrl/⌘+Shift+I/J/C (and Firefox's K/E), Ctrl/⌘+U, and
 *      the ⌘+⌥ variants on a Mac. Matched on `e.code`, because ⌥ turns the
 *      letter in `e.key` into a symbol.
 *   2. The context menu, which is where "Inspect" lives.
 *   3. Detection by a `debugger` statement, which costs nothing with devtools
 *      closed and PAUSES with them open — docked, undocked, any zoom. It is
 *      built with Function() because the production minifier strips a literal
 *      `debugger`. Deactivating breakpoints defeats it: deterrence, not a lock.
 *
 * ⚠️ No window-size heuristic, deliberately. A detection here hides the whole
 * LMS, so a false positive locks a student out — and the outer/inner size gap
 * is wide for plenty of innocent reasons: Chrome's side panel, Edge's sidebar,
 * a docked bookmarks panel, browser zoom. utils/playbackGuard.js switched the
 * same heuristic off for the same reason.
 *
 * @param {object}  opts
 * @param {boolean} opts.active
 */
const CHECK_EVERY_MS = 1000;
// A trap call returns in microseconds unless the debugger stopped it.
const PAUSE_THRESHOLD_MS = 100;

let trap = null;
const debuggerPaused = () => {
  try {
    // eslint-disable-next-line no-new-func
    trap = trap || new Function("debugger");
    const start = performance.now();
    trap();
    return performance.now() - start > PAUSE_THRESHOLD_MS;
  } catch {
    return false; // a CSP without 'unsafe-eval' — shortcuts and menu still blocked
  }
};

const SHORTCUT_CODES = new Set(["KeyI", "KeyJ", "KeyC", "KeyK", "KeyE"]);

const isInspectorShortcut = (e) => {
  if (e.key === "F12") return true;
  const mod = e.ctrlKey || e.metaKey;
  if (!mod) return false;
  if (e.code === "KeyU" && !e.shiftKey) return true; // view source
  if (e.shiftKey && SHORTCUT_CODES.has(e.code)) return true; // Ctrl+Shift+I…
  if (e.metaKey && e.altKey && (SHORTCUT_CODES.has(e.code) || e.code === "KeyU")) return true; // ⌘⌥I…
  return false;
};

export default function useDevToolsGuard({ active }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!active) {
      setOpen(false);
      return undefined;
    }

    const onKeyDown = (e) => {
      if (!isInspectorShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onContextMenu = (e) => e.preventDefault();
    const check = () => {
      if (document.hidden) return;
      setOpen(debuggerPaused());
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    const id = window.setInterval(check, CHECK_EVERY_MS);
    check();

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      window.clearInterval(id);
    };
  }, [active]);

  return open;
}
